import React, { useEffect, useMemo, useRef, useState } from "react";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { TeamAccess } from "target_wasm";
import { useShallow } from "zustand/react/shallow";
import { createLogger } from "@/debug/DebugingLogger";
import { useNavigation } from "@/contexts/NavigationProvider";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { AssetIcon, TEAM_ASSETS } from "@/components/common/AssetIcon";
import { AppearancePicker } from "@/components/common/AppearancePicker";
import { TooltipButton } from "@/components/common/TooltipButton";
import { Inline } from "@/components/common/Stack";
import { LoadingState } from "@/components/common/LoadingState";
import { EditableName } from "@/components/canvas/utils/EditableName";
import { TeamNavigator } from "./TeamNavigator";
import { TeamModels } from "./TeamModels";
import {
  ColumnBottomSpacer,
  ColumnTopSpacer,
} from "@/components/main/pages/pageColumn";
import { TeamMembersDialog } from "./TeamMembersDialog";
import { TeamDescription } from "./TeamDescription";
import { useTeamActions } from "./useTeamActions";

const log = createLogger("TeamPage");

interface TeamPageProps {
  team: TeamAccess;
}

/// Single-team view. A sidebar-less centered column (matching Overview /
/// Datasets): the team switcher, a team-identity header with inline admin
/// actions + description, then the team's models as a `ModelRow` list.
export const TeamPage: React.FC<TeamPageProps> = ({ team }) => {
  const { navigateToPage, navigateToTeam, teamsUpdateTrigger } =
    useNavigation();
  const [activeTeam, setActiveTeam] = useState<TeamAccess>(team);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  const {
    teams: allTeams,
    teamMembers,
    teamModels,
    teamDetailsLoading,
    fetchTeamsList,
    fetchTeamMembers,
    fetchTeamModels,
  } = useTeamsStore(
    useShallow((state) => ({
      teams: state.teams,
      teamMembers: state.teamMembers,
      teamModels: state.teamModels,
      teamDetailsLoading: state.teamDetailsLoading,
      fetchTeamsList: state.fetchTeamsList,
      fetchTeamMembers: state.fetchTeamMembers,
      fetchTeamModels: state.fetchTeamModels,
    })),
  );

  const teamData = useMemo(() => {
    try {
      const details = activeTeam.team_details_js();
      return {
        teamId: activeTeam.team_id_str(),
        isAdmin: activeTeam.relation_display_js() === "Admin",
        roleLabel: String(activeTeam.relation_display_js()),
        teamName: String(details.team_name_js()),
        teamColor: String(details.team_color_js()),
        teamAsset: String(details.team_asset_js()),
      };
    } catch (error) {
      log.error("Error extracting team data", error);
      return null;
    }
  }, [activeTeam]);

  const teamId = teamData?.teamId || "";
  const isAdmin = teamData?.isAdmin || false;
  const members = teamMembers[teamId] || [];
  const modelsCount = teamModels[teamId]?.length;
  const isLoadingDetails = teamDetailsLoading[teamId] || false;

  const actions = useTeamActions(activeTeam, members);

  useEffect(() => setActiveTeam(team), [team]);

  useEffect(() => {
    if (!teamId) return;
    fetchTeamMembers(teamId);
    fetchTeamModels(teamId, false, true);
  }, [teamId, fetchTeamMembers, fetchTeamModels]);

  useEffect(() => {
    if (allTeams.length === 0) fetchTeamsList({ showLoading: false });
  }, [allTeams.length, fetchTeamsList]);

  useEffect(() => {
    if (teamsUpdateTrigger > 0) {
      fetchTeamsList({ showLoading: false, refresh: true });
    }
  }, [teamsUpdateTrigger, fetchTeamsList]);

  const shouldShowLoading =
    !teamData || !teamId || (isLoadingDetails && modelsCount === undefined);
  if (shouldShowLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoadingState size="large" isLoading label="Loading team..." />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6">
      <ColumnTopSpacer />
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "45rem",
          paddingTop: "2.5rem",
          paddingBottom: "2.5rem",
        }}
      >
        <div className="shrink-0 mb-6">
          <TeamNavigator
            teamName={teamData.teamName}
            currentTeamId={teamId}
            teams={allTeams}
            onNavigateToTeams={() => navigateToPage("Teams")}
            onSelectTeam={(targetTeamId) => {
              if (targetTeamId !== teamId) navigateToTeam(targetTeamId);
            }}
            onShowTeamDetails={() => {}}
          />
        </div>

        <div className="shrink-0 mb-6">
          {/* gap-2 + a 24px (size-6) icon slot mirror the Description section's
              heading geometry, so the team icon/name and the description
              icon/label/body line up in one left rail. */}
          <div className="flex items-center gap-2">
            <div className="relative shrink-0" ref={iconRef}>
              <span
                role={isAdmin ? "button" : undefined}
                aria-label={isAdmin ? "Change team appearance" : undefined}
                className={`size-6 flex items-center justify-center rounded-app ${isAdmin ? "hover:bg-bg-hover cursor-pointer" : ""}`}
                onClick={() => {
                  if (isAdmin) setIsPickerOpen(true);
                }}
              >
                <AssetIcon
                  size="sm"
                  color={teamData.teamColor}
                  asset={teamData.teamAsset}
                />
              </span>
              <AppearancePicker
                currentColor={teamData.teamColor}
                currentAsset={teamData.teamAsset}
                assets={TEAM_ASSETS}
                onColorChange={(color) => actions.editAppearance({ color })}
                onAssetChange={(asset) => actions.editAppearance({ asset })}
                isOpen={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                disabled={!isAdmin}
                anchorRef={iconRef}
              />
            </div>

            <span
              className="min-w-0 flex items-center text-fg"
              style={{
                fontSize: "var(--font-size)",
                fontWeight: "var(--font-weight-medium)",
              }}
            >
              <EditableName
                name={teamData.teamName || "Untitled team"}
                isEditing={isEditingName}
                canEdit={isAdmin}
                editTrigger="icon"
                editColor={teamData.teamColor || undefined}
                setIsEditing={setIsEditingName}
                update={() => {}}
                commit={actions.commitName}
                abort={() => setIsEditingName(false)}
              />
            </span>

            <span
              className="text-small text-fg-muted shrink-0"
              style={{ fontWeight: "var(--font-weight-regular)" }}
            >
              {teamData.roleLabel}
            </span>

            <span className="flex-1" />

            <Inline gap="sm" className="shrink-0">
              {isAdmin && (
                <TooltipButton
                  title="Manage members"
                  variant="contextual"
                  size="xs"
                  aria-label="Manage members"
                  onClick={() => setIsMembersDialogOpen(true)}
                >
                  <PersonAddOutlinedIcon />
                </TooltipButton>
              )}
              <TooltipButton
                title="Leave team"
                variant="contextual"
                size="xs"
                aria-label="Leave team"
                onClick={actions.requestLeave}
              >
                <LogoutRoundedIcon />
              </TooltipButton>
              {actions.permissions.canDelete && (
                <TooltipButton
                  title="Delete team"
                  variant="contextual"
                  size="xs"
                  aria-label="Delete team"
                  onClick={actions.requestDelete}
                >
                  <DeleteOutlineOutlinedIcon />
                </TooltipButton>
              )}
            </Inline>
          </div>

          <div className="mt-2">
            {/* Flush: the header has no chevron, so align the description icon
                under the team icon and its label + body under the name. pr-0 so
                the "+ Add a description" pill lines up with the full-width
                search bar / New model pill below. */}
            <TeamDescription
              teamId={teamId}
              canEdit={isAdmin}
              headingPl="pl-0"
              bodyPl="pl-8"
              pr="pr-0"
            />
          </div>
        </div>

        <TeamModels team={activeTeam} isAdmin={isAdmin} />
      </div>
      <ColumnBottomSpacer />

      {actions.dialogs}

      <TeamMembersDialog
        open={isMembersDialogOpen}
        onClose={() => setIsMembersDialogOpen(false)}
        teamId={teamId}
        teamName={teamData.teamName}
        isAdmin={isAdmin}
      />
    </div>
  );
};
